import React, { useState } from 'react';
import { getApiBaseUrl } from '../config/api';
import Snackbar from './Snackbar';
import './AgentLogin.css';

const AgentLogin = ({ onLogin, onBackToMain, onSwitchToRegistration }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: ''
  });
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  // Persistent snackbar for errors (e.g. email not found)
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('error');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Get API base URL
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/api/agent/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Login failed. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      const data = await response.json();

      if (data.success) {
        // Check if it's an employee or agent
        if (data.userType === 'employee') {
          // Store employee token and user info
          localStorage.setItem('employeeToken', data.token);
          localStorage.setItem('employeeUser', JSON.stringify(data.user));
          localStorage.setItem('isEmployee', 'true');
          
          // Pass user data with type indicator
          onLogin({ ...data.user, userType: 'employee' });
        } else {
          // Store agent token and user info
          localStorage.setItem('agentToken', data.token);
          localStorage.setItem('agentUser', JSON.stringify(data.user));
          localStorage.removeItem('isEmployee');
          onLogin(data.user);
        }
      } else {
        setError(data.error || 'Invalid username or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="agent-login-modern">
      {/* Animated Background */}
      <div className="login-background">
        <div className="bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      </div>

      <div className="login-wrapper">
        <div className="login-card">
          {/* Logo and Header Section */}
          <div className="login-header-modern">
            <div className="logo-container">
              <div className="logo-icon-modern">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <h1 className="login-title">Agent Portal</h1>
            <p className="login-subtitle">Welcome back! Please sign in to continue</p>
          </div>

          {/* Login Form */}
          <form className="login-form-modern" onSubmit={handleSubmit}>
            {/* Username Field */}
            <div className="input-group-modern">
              <div className={`input-wrapper ${focusedField === 'username' || credentials.username ? 'focused' : ''}`}>
                <label htmlFor="username" className="floating-label">
                  <span className="label-icon">👤</span>
                  <span className="label-text">Username</span>
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={credentials.username}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('username')}
                  onBlur={() => setFocusedField(null)}
                  placeholder=" "
                  required
                  disabled={isLoading}
                  autoComplete="username"
                  className="input-modern"
                />
                <div className="input-underline"></div>
              </div>
            </div>

            {/* Password Field */}
            <div className="input-group-modern">
              <div className={`input-wrapper ${focusedField === 'password' || credentials.password ? 'focused' : ''}`}>
                <label htmlFor="password" className="floating-label">
                  <span className="label-icon">🔒</span>
                  <span className="label-text">Password</span>
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  placeholder=" "
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  className="input-modern"
                />
                <button
                  type="button"
                  className="password-toggle-modern"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  )}
                </button>
                <div className="input-underline"></div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message-modern" role="alert">
                <svg className="error-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span className="error-text">{error}</span>
              </div>
            )}

            {/* Forgot Password Link */}
            <div className="forgot-password-wrapper">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="forgot-password-link-modern"
                disabled={isLoading}
              >
                Forgot your password?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="submit-button-modern"
              disabled={isLoading || !credentials.username || !credentials.password}
            >
              {isLoading ? (
                <>
                  <svg className="spinner-svg" viewBox="0 0 24 24">
                    <circle className="spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="32" strokeDashoffset="32">
                      <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                      <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
                    </circle>
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </button>

            {/* Sign-up / Registration */}
            {onSwitchToRegistration && (
              <div className="registration-prompt">
                <p className="registration-text">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={onSwitchToRegistration}
                    className="register-link-modern"
                    disabled={isLoading}
                  >
                    Create Account
                  </button>
                </p>
              </div>
            )}
          </form>

          {/* Back to Main Button */}
          {onBackToMain && (
            <button
              className="back-to-main-modern"
              onClick={onBackToMain}
              disabled={isLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>Back to Main</span>
            </button>
          )}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal-overlay-modern" onClick={() => {
          if (!forgotPasswordLoading) {
            setShowForgotPassword(false);
            setForgotPasswordError('');
            setForgotPasswordSuccess(false);
            setForgotPasswordData({ email: '' });
          }
        }}>
          <div className="modal-content-modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-modern">
              <h2>Reset Password</h2>
              <button
                type="button"
                className="modal-close-modern"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordError('');
                  setForgotPasswordSuccess(false);
                  setForgotPasswordData({ email: '' });
                }}
                disabled={forgotPasswordLoading}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            
            {forgotPasswordSuccess ? (
              <div className="success-content-modern">
                <div className="success-icon-modern">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <h3>Check Your Email</h3>
                <p>We've sent a password reset link to your email address. Please check your inbox and follow the instructions.</p>
                <button
                  type="button"
                  className="close-success-btn-modern"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordSuccess(false);
                    setForgotPasswordData({ email: '' });
                  }}
                >
                  Got it
                </button>
              </div>
            ) : (
              <form
                className="forgot-password-form-modern"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setForgotPasswordLoading(true);
                  setForgotPasswordError('');
                  
                  try {
                    const API_BASE_URL = getApiBaseUrl();
                    const response = await fetch(`${API_BASE_URL}/api/agent/forgot-password`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        email: forgotPasswordData.email
                      }),
                    });
                    
                    const data = await response.json();
                    
                    if (response.ok && data.success) {
                      setForgotPasswordSuccess(true);
                      setForgotPasswordError('');
                      setShowSnackbar(false);
                    } else {
                      const errorMsg = data.error || 'Failed to send reset link. Please try again.';
                      setForgotPasswordError(errorMsg);
                      // Show intelligent error in persistent snackbar (e.g. email not found)
                      setSnackbarMessage(errorMsg);
                      setSnackbarType('error');
                      setShowSnackbar(true);
                    }
                  } catch (err) {
                    console.error('Forgot password error:', err);
                    const errorMsg = 'Failed to send reset link. Please try again.';
                    setForgotPasswordError(errorMsg);
                    setSnackbarMessage(errorMsg);
                    setSnackbarType('error');
                    setShowSnackbar(true);
                  } finally {
                    setForgotPasswordLoading(false);
                  }
                }}
              >
                <div className="input-group-modern">
                  <div className={`input-wrapper ${forgotPasswordData.email ? 'focused' : ''}`}>
                    <label htmlFor="reset-email" className="floating-label">
                      <span className="label-icon">✉️</span>
                      <span className="label-text">Email Address</span>
                    </label>
                    <input
                      type="email"
                      id="reset-email"
                      value={forgotPasswordData.email}
                      onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, email: e.target.value })}
                      placeholder=" "
                      required
                      disabled={forgotPasswordLoading}
                      autoComplete="email"
                      className="input-modern"
                    />
                    <div className="input-underline"></div>
                  </div>
                </div>
                
                {forgotPasswordError && (
                  <div className="error-message-modern" role="alert">
                    <svg className="error-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span className="error-text">{forgotPasswordError}</span>
                  </div>
                )}
                
                <div className="modal-actions-modern">
                  <button
                    type="button"
                    className="cancel-btn-modern"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordError('');
                      setForgotPasswordData({ email: '' });
                    }}
                    disabled={forgotPasswordLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-btn-modern"
                    disabled={forgotPasswordLoading || !forgotPasswordData.email}
                  >
                    {forgotPasswordLoading ? (
                      <>
                        <svg className="spinner-svg" viewBox="0 0 24 24">
                          <circle className="spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="32" strokeDashoffset="32">
                            <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                            <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
                          </circle>
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Reset Link</span>
                        <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Persistent snackbar for forgot-password errors (e.g. email not found) */}
      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
          duration={0}
        />
      )}
    </div>
  );
};

export default AgentLogin;
